import center from '@turf/center';
import _clamp from 'lodash.clamp';

const VALID_GEOMS = ['POINT', 'LINESTRING', 'POLYGON', 'MULTIPOLYGON'];

export const isValidGeomType = (geomType) => VALID_GEOMS.includes(`${geomType}`.toUpperCase());
export const validateGeomType = (geomType) => {
  if (!isValidGeomType(geomType)) {
    throw new Error(`Invalid geom type: ${geomType}`);
  }
};

export const caseGeomType = (geomType) => {
  if (!isValidGeomType(geomType)) throw new Error(`Invalid geom type: ${geomType}`);

  const upperGeomType = geomType.toUpperCase();

  if (upperGeomType === 'POINT') return 'Point';
  if (upperGeomType === 'LINESTRING') return 'LineString';
  if (upperGeomType === 'MULTIPOLYGON') return 'MultiPolygon';
  return 'Polygon';
};

const getGeomType = (element) => {
  if (!element) return '';

  if (typeof element === 'string') return element;

  const geomType =
    element.geomType ?? element?.properties?.geomType ?? element?.geometry?.type ?? element?.type;

  if (typeof geomType === 'string') {
    return geomType;
  }

  return '';
};

export const elementIsGeomType = (geomType) => (element) =>
  getGeomType(element).toUpperCase() === geomType.toUpperCase();

export const isLineString = elementIsGeomType('LineString');
export const isPoint = elementIsGeomType('Point');
const _isPolygon = elementIsGeomType('Polygon');
const _isMultiPolygon = elementIsGeomType('MultiPolygon');
export const isPolygon = (element) => _isPolygon(element) || _isMultiPolygon(element);

export const getPixelBboxFromPoint = ({ point, halfPixels = 10 }) => {
  const { x, y } = point;

  // create a square polygon around a point where each side is <halfPixels>
  // away from the center
  const pixelBbox = [
    [x - halfPixels, y + halfPixels],
    [x + halfPixels, y + halfPixels],
    [x + halfPixels, y - halfPixels],
    [x - halfPixels, y - halfPixels],
    [x - halfPixels, y + halfPixels],
  ];

  return pixelBbox;
};

export const getLatLonPolygonFromPixel = ({ point, map, halfPixels = 10 }) => {
  const pixelBbox = getPixelBboxFromPoint({ point, halfPixels });
  const latLonCoordinates = pixelBbox.map((pt) => map.unproject(pt).toArray());

  return {
    coordinates: [latLonCoordinates],
    type: 'Polygon',
  };
};

export const getGeoJsonGeometry = (coordinates, geomType) => {
  return {
    type: caseGeomType(geomType),
    coordinates,
  };
};

export const getCenter = (coordinates, geomType) => {
  const geoJsonFeature = getGeoJsonGeometry(coordinates, geomType);
  return center(geoJsonFeature).geometry.coordinates;
};

// This is a complementary function that can be used alongside map.fitBounds -
// adds a little more space so the bounded area is in the middle of the screen
// with room left around the edges
export const addBufferToBounds = (bounds) => {
  const midpoint = (a, b) => (a + b) / 2;
  const bufferedRadius = (a, b) => (1.5 * Math.abs(a - b)) / 2;
  const [lng1, lat1, lng2, lat2] = bounds;
  const midLng = midpoint(lng1, lng2);
  const midLat = midpoint(lat1, lat2);
  const radLng = bufferedRadius(lng1, lng2);
  const radLat = bufferedRadius(lat1, lat2);
  return [
    midLng - radLng,
    _clamp(midLat - radLat, -90, 90),
    midLng + radLng,
    _clamp(midLat + radLat, -90, 90),
  ];
};
